import { Fragment, useMemo } from 'react'

import { Menu, Transition } from '@headlessui/react'

import { useGame } from 'store/game/hooks'

import { Icon } from '../Icon'
import { Typography } from '../Typography'

export function DropDown({
  value,
  onChange,
  error,
}: {
  value?: string
  onChange: (val: string) => void
  error?: string
}) {
  const { games, isLoading } = useGame()

  const selectedGame = useMemo(() => {
    return games?.find((game) => game._id === value)
  }, [games, value])

  return (
    <div className="w-full">
      <Typography variant="caption" className="mb-2 text-dark-typo-gray">
        SELECT GAME
      </Typography>
      <Menu as="div" className="relative inline-block text-left w-full">
        <div>
          <Menu.Button className={`flex items-center justify-between w-full bg-dark-card border-2 ${
            error ? 'border-red' : selectedGame ? 'border-green' : 'border-dark-border'
          } hover:border-green rounded-xl px-4 py-3 transition-colors focus:outline-none`}>
            {isLoading ? (
              <Typography variant="body" fontWeight="semibold" className="text-dark-typo-gray">
                Loading games...
              </Typography>
            ) : selectedGame ? (
              <div className="flex items-center gap-3">
                <img
                  src={selectedGame.avatar?.url || selectedGame.cover_photo?.url}
                  alt={selectedGame.name}
                  className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                />
                <Typography variant="body" fontWeight="semibold" className="text-white">
                  {selectedGame.name}
                </Typography>
              </div>
            ) : (
              <Typography variant="body" fontWeight="semibold" className="text-dark-typo-gray">
                {games && games.length > 0 ? 'Choose a game...' : 'No games available'}
              </Typography>
            )}
            <Icon size={20} color={selectedGame ? 'primary' : 'secondary'} name="chevronDown" />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="mt-1 border border-dark-border absolute right-0 w-full bg-dark-card origin-top-right divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="px-1 py-1 max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="px-3 py-4 text-center">
                  <Typography variant="body" className="text-dark-typo-gray">
                    Loading games...
                  </Typography>
                </div>
              ) : games && games.length > 0 ? (
                games.map((game) => (
                  <Menu.Item key={game._id}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={`${
                          active
                            ? 'bg-dark-gray-primary'
                            : 'hover:bg-dark-gray-primary'
                        } group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors`}
                        onClick={() => {
                          onChange(game._id)
                        }}
                      >
                        <img
                          src={game.avatar?.url || game.cover_photo?.url}
                          alt={game.name}
                          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                        />
                        <Typography
                          variant="body"
                          fontWeight="medium"
                          className={active ? 'text-white' : 'text-dark-typo-secondary'}
                        >
                          {game.name}
                        </Typography>
                      </button>
                    )}
                  </Menu.Item>
                ))
              ) : (
                <div className="px-3 py-4 text-center">
                  <Typography variant="body" className="text-dark-typo-gray">
                    No games available
                  </Typography>
                </div>
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      {error && (
        <Typography variant="caption-xs" className="text-red mt-1">
          {error}
        </Typography>
      )}
    </div>
  )
}
